import { faEye, faEyeSlash, faLock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AlertTitle, Box, Button, IconButton, InputAdornment, Link, styled, Table, TableBody, TableCell, TableRow, TextField, TextFieldProps, Typography } from '@mui/material';
import { grey } from "@mui/material/colors";
import { Dispatch, FC, HTMLInputTypeAttribute, ReactNode, SetStateAction, useCallback, useContext, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { ForgotPassword } from "../api/dtos/dtos";
import Center from "../components/common/Center";
import Hr from "../components/common/HorizontalRule";
import { UserFormatError } from "../constants/exceptions/CustomExceptions";
import { ERROR_TIMEOUT, SUCCESS_TIMEOUT } from "../constants/styles/AlertConstants";
import AuthContext from "../contexts/auth/AuthContext";
import AlertContext from "../contexts/styles/AlertContext";
import useServiceStackClient from "../hooks/api/useServiceStackClient";
import { AppSettings } from '../AppSettings';

interface IStyledTableRowProps {
    primaryLabel: string;
    secondaryLabel?: string;

    type?: HTMLInputTypeAttribute;

    fieldState?: FieldState;

    select?: boolean;
    items?: ReactNode[];
    
    disabled?: boolean;

    value: any;
    onChange?: Dispatch<SetStateAction<any>>
}

type FieldState = {
    state?: "error" | "warning";
    message?: string;
}

const StyledTableCell = styled(TableCell)(() => ({
    padding: '8px',
    border: 'none'
}));

const StyledTextField = styled(TextField, { shouldForwardProp: (props) => props !== 'stateColor' })<TextFieldProps & { stateColor?: string }>(({ theme, stateColor, disabled }) => ({
    '& label': {
        color: stateColor === 'error' ?
                theme.palette.error.main :
            stateColor === 'warning' ?
                theme.palette.warning.main :
                grey[700],
    },
    '& p': {
        color: stateColor === 'error' ?
                theme.palette.error.main :
            stateColor === 'warning' ?
                theme.palette.warning.main :
                grey[700]
    },
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: stateColor === 'error' ? 
                    `${theme.palette.error.main}77` :
                stateColor === 'warning' ?
                    theme.palette.warning.main :
                    'rgba(0, 0, 0, 0.2)',
        },
        '&:hover fieldset': {
            borderColor: stateColor === 'error' ? 
                    theme.palette.error.main :
                stateColor === 'warning' ?
                    theme.palette.warning.main :
                disabled ?
                    'rgba(0, 0, 0, 0.2)' :
                    'rgb(0, 0, 0)',
        },
        '&.Mui-focused fieldset': {
            borderColor: stateColor === 'error' ? 
                    theme.palette.error.main :
                stateColor === 'warning' ?
                    theme.palette.warning.main :
                    theme.palette.primary.main
        },
    },
}));

const ForgotPasswordContainer = styled(Box)(() => ({
    padding: '30px 14px 8px 14px'
}));

const StyledConfirmButton = styled(Button)(({ theme }) => ({
    maxWidth: '100px',
    minWidth: '100px',
    minHeight: '40px',
    maxHeight: '40px',
    backgroundColor: theme.palette.primary.main,
    color: grey[100],
    marginLeft: '30px',

    "&:hover": {
        backgroundColor: theme.palette.primary.main,
        boxShadow: '0px 0px 6px 1px rgba(75,90,35,0.5)'
    },

    "&:active": {
        backgroundColor: theme.palette.secondary.main
    }
}));

const StyledTableRow: FC<IStyledTableRowProps> = ({
    primaryLabel, 
    secondaryLabel,
    type,
    select,
    items,
    fieldState,
    disabled,
    value,
    onChange
}) => {
    const [showPassword, setShowPassword] = useState<boolean>(false);

    return (
        <TableRow>
            <StyledTableCell sx={{ minWidth: '124px', maxWidth: '124px' }}>
                {primaryLabel}:
            </StyledTableCell>
            <StyledTableCell width='100%'>
                {
                    select ? 
                        <StyledTextField
                            select
                            stateColor={fieldState?.state}
                            color={fieldState?.state}
                            helperText={fieldState?.message}
                            disabled={!!disabled}
                            label={secondaryLabel ?? ""}
                            fullWidth
                            size='small'
                            value={value}
                            onChange={onChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        >
                            {items}
                        </StyledTextField> :
                        <StyledTextField
                            id={`Text-Field-${primaryLabel.replaceAll(' ', '-')}`}
                            inputProps={{
                                autoComplete: 'password',
                            }}
                            InputProps={{
                                endAdornment: type === 'password' ? (
                                    <InputAdornment position="end">
                                        <IconButton 
                                            disableRipple
                                            sx={{ width: '24px', heigh: '24px' }}    
                                            onMouseDown={() => setShowPassword(true)} 
                                            onMouseUp={() => setShowPassword(false)} 
                                            onMouseLeave={() => setShowPassword(false)}
                                        >
                                            {
                                                showPassword ? 
                                                    <FontAwesomeIcon icon={faEye} style={{ fontSize: '1.2rem' }} /> : 
                                                    <FontAwesomeIcon icon={faEyeSlash} style={{ fontSize: '1.2rem' }} />
                                            }
                                        </IconButton>
                                    </InputAdornment>
                                ) :
                                disabled ? (
                                    <InputAdornment position="end">
                                        <FontAwesomeIcon icon={faLock} style={{ fontSize: '1.2rem' }}/>
                                    </InputAdornment>
                                ) : 
                                null
                            }}
                            autoComplete='password'
                            type={type === 'password' ? !showPassword ? 'password' : 'text' : type}
                            stateColor={fieldState?.state}
                            color={fieldState?.state}
                            helperText={fieldState?.message}
                            disabled={!!disabled}
                            label={secondaryLabel ?? ""}
                            fullWidth
                            size='small'
                            value={value}
                            onChange={onChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                }
            </StyledTableCell>
        </TableRow>
    )
}

const ForgotPasswordContent: FC = () => {

    //
    // -- Hooks -- 
    const client = useServiceStackClient();
    const { species: speciesName } = useParams();

    // -- Context --
    const { addAlert } = useContext(AlertContext);

    // -- Registration state --
    const [forgotPasswordDetails, setForgotPasswordDetails] = useState<ForgotPassword | null>(null);
    const [forgotPasswordDetailsState, setForgotPasswordDetailsState] = useState<Record<string, FieldState>>({});
    const [disabled, setDisabled] = useState<boolean>(false);

    // Validate the forgot passsword fields
    const validateForgotPasswordField = useCallback((field: string, value: any, warn?: boolean): boolean => {
        // Confirm the field is a key of user registration type
        if (field as keyof ForgotPassword) {
            
            // Update forgot password email
            if (field === 'email') {
                const regex = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/

                const valid = regex.test(value);
                const errorMessage = !value || value === '' ?
                    'Email is required.' :
                    'Invalid email.'

                if (!valid) {
                    if (warn) {
                        setForgotPasswordDetailsState(c => {
                            return {
                                ...c,
                                [field]: {
                                    state: 'warning',
                                    message: 'Invalid email'
                                }
                            };
                        });
                    }
                    else {
                        setForgotPasswordDetailsState(c => {
                            return {
                                ...c,
                                field: {
                                    state: 'error',
                                    message: errorMessage
                                }
                            };
                        });
                    }
                }
                else {
                    setForgotPasswordDetailsState(c => {
                        delete c[field];
                        return c;
                    })
                }

                return valid;
            }
            // Otherwise, clear all errors/warnings
            else {
                setForgotPasswordDetailsState({});
            }

            return true;
        }

        return false;

    }, [setForgotPasswordDetailsState])

    const handleForgotPasswordChange = useCallback((field: string, value: any) => {
        validateForgotPasswordField(field, value, true);

        setForgotPasswordDetails(c => {
            return new ForgotPassword({
                ...c,
                [field]: value
            });
        })
    }, [validateForgotPasswordField, setForgotPasswordDetails]);

    const handleForgotPasswordSubmit = useCallback(async () => {
        if (disabled) {
            return;
        }
        setDisabled(true);

        let valid = true;
        valid = validateForgotPasswordField('email', forgotPasswordDetails?.email);
        if (valid) {
            try {
                // Make forgot password request
                await client.post(
                    new ForgotPassword({
                        email: forgotPasswordDetails?.email
                    })
                );
                
                // Alert user of success
                addAlert({
                    content: <AlertTitle>
                            Forgot password request successful.
                        </AlertTitle>,
                    dismissable: true,
                    severity: 'success',
                    timeout: SUCCESS_TIMEOUT
                });

                setForgotPasswordDetails(c => {
                    return new ForgotPassword({
                        ...c,
                        email: ""
                    });
                });
                setForgotPasswordDetailsState({});
            } catch (error: any) {
                if (error?.responseStatus) {
                    if (error.responseStatus.errorCode === UserFormatError.errorCode) {
                        let newState: Record<string, FieldState> = {};

                        for (var key of Object.keys(error.responseStatus.meta)) {
                            newState[key] = {
                                message: error.responseStatus.meta[key],
                                state: 'error'
                            }
                        }

                        addAlert({
                            content: <AlertTitle>
                                    {newState[Object.keys(newState)[0]].message}
                                </AlertTitle>,
                            dismissable: true,
                            severity: 'error',
                            timeout: ERROR_TIMEOUT
                        });

                        setForgotPasswordDetailsState(newState);
                    }
                    else {
                        addAlert({
                            content: <AlertTitle>
                                    {error.responseStatus.message}
                                </AlertTitle>,
                            dismissable: true,
                            severity: 'error',
                            timeout: ERROR_TIMEOUT
                        });
                    }
                }
                else {
                    addAlert({
                        content: <AlertTitle>
                                {error.message}
                            </AlertTitle>,
                        dismissable: true,
                        severity: 'error',
                        timeout: ERROR_TIMEOUT
                    });
                }
            }
        }
        else {
            addAlert({
                content: <AlertTitle>
                        Please enter a valid email.
                    </AlertTitle>,
                dismissable: true,
                severity: 'error',
                timeout: ERROR_TIMEOUT
            });
        }
        setDisabled(false);
    }, [
        client, 
        forgotPasswordDetails, 
        validateForgotPasswordField, 
        addAlert, 
        setForgotPasswordDetails,
        setForgotPasswordDetailsState, 
        disabled, 
        setDisabled
    ]);

    return (
        <ForgotPasswordContainer>
            <Typography color="#333" variant='h2'>Forgot Password</Typography>
            <Hr color={grey[400]} sx={{ margin: '40px 0' }} />

            <Typography
                sx={{
                    padding: '10px'
                }}
            >
                Please enter your email address in the box below and click the Send button. Your username and a new password will be emailed to you. If you have not received the email in a few minutes please check your spam or junk folder.
            </Typography>
            <Typography
                sx={{
                    padding: '10px'
                }}
            >
                If you have any difficulties with your password, please contact the FeralScan team – email <Link href={`mailto:${AppSettings.systemMailAddress}`}>{AppSettings.systemMailAddress}</Link> or visit <Link href={`/${speciesName}/content/${speciesName?.replace("scan", "")?.replace("alert", "")}_contacts`}>Contact us</Link>
            </Typography>

            <Table>
                <TableBody>
                    <StyledTableRow
                        primaryLabel="Email"
                        fieldState={forgotPasswordDetailsState['email']}
                        value={forgotPasswordDetails?.email}
                        onChange={(v) => handleForgotPasswordChange('email', v.target.value)}
                    />
                </TableBody>
            </Table>

            <Center ha='end' m='15px 10px'>
                <StyledConfirmButton
                    onClick={handleForgotPasswordSubmit}
                    disabled={disabled}
                >
                    <Typography fontWeight='600'>Send</Typography>
                </StyledConfirmButton>
            </Center>
        
        </ForgotPasswordContainer>
    )
}

const ForgotPasswordPage: FC = () => {
    const { user } = useContext(AuthContext);
    const { species: speciesName } = useParams();

    if (user) {
        return <Navigate to={"/" + speciesName + "/profile"} replace />
    }
    return (<ForgotPasswordContent />)
}
export default ForgotPasswordPage
